import type * as Sentry from '@sentry/capacitor';
import type { Span, SpanAttributes } from '@sentry/core';
import { SPAN_STATUS_ERROR, SPAN_STATUS_OK } from '@sentry/core';

import { ELogLevel } from '../../../shared/constants';
import { ILogEntry } from '../../types';

import { mapELogLevelToSentryLevel } from './index';

/**
 * Sentry integration for logger
 * Sends log entries to Sentry for error tracking
 */
export class SentryIntegration {
	constructor(
		private sentry: typeof Sentry,
		private sentryMinLevel: ELogLevel
	) {}

	/**
	 * Send event to Sentry
	 * @param entry - Log entry to send
	 */
	public captureEvent(entry: ILogEntry): void {
		if (entry.level < this.sentryMinLevel) {
			return;
		}

		/** Set logger context */
		this.sentry.setContext('logger', {
			...entry.context,
			...entry.metadata,
		});

		/** Set namespace as tag */
		if (entry.namespace) {
			this.sentry.setTag('logger.namespace', entry.namespace);
		}

		/** Our own log message as a searchable tag. The issue title is the raw
		 *  error value (err.message), so this is how an issue is traced back to the
		 *  log call site — filter/group by `logger.message` in Sentry. */
		this.sentry.setTag('logger.message', entry.message);

		/** Set event as tag for filtering/grouping */
		if (entry.event) {
			this.sentry.setTag('logger.event', entry.event);
		}

		const error = entry.metadata?.['error'];

		if (error instanceof Error) {
			this.sentry.captureException(error);
		} else {
			this.sentry.captureMessage(entry.message, {
				level: 'error',
			});
		}
	}

	/**
	 * Add breadcrumb to Sentry
	 * @param entry - Log entry to add as breadcrumb
	 */
	public addBreadcrumb(entry: ILogEntry): void {
		if (entry.level < this.sentryMinLevel) {
			return;
		}

		this.sentry.addBreadcrumb({
			message: entry.message,
			category: entry.category || 'logger',
			level: mapELogLevelToSentryLevel(entry.level),
			data: entry.metadata,
		});
	}

	/**
	 * Track performance with Sentry
	 * @param name - Operation name
	 * @param operation - Operation type
	 * @param callback - Async callback to execute
	 * @param attributes - Additional attributes
	 * @returns Result of callback
	 */
	public trackPerformance<T>(
		name: string,
		operation: string,
		callback: (span?: Span) => Promise<T>,
		attributes?: SpanAttributes
	): Promise<T> {
		return this.sentry.startSpan(
			{
				name,
				op: operation,
				attributes,
			},
			async (span: Span) => {
				try {
					const result = await callback(span);
					span.setStatus({ code: SPAN_STATUS_OK });
					return result;
				} catch (error) {
					/** Use a canonical Sentry status; a free-form message renders as "unknown". */
					span.setStatus({
						code: SPAN_STATUS_ERROR,
						message: 'internal_error',
					});
					throw error;
				}
			}
		);
	}
}
